"use client";

import type { Category } from "@/types/prisma";
import {
  Check,
  Copy,
  Edit3,
  ExternalLink,
  Link2,
  Loader2,
  Megaphone,
  Plus,
  Search,
  Sliders,
  Tag,
  Trash2,
  X
} from "lucide-react";
import Link from "next/link";
import { FormEvent, useMemo, useState } from "react";
import { toast } from "sonner";

import { AdminPagination } from "@/components/admin/admin-pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getCategoryPermanentLink, PRIMARY_CATEGORY_METAS } from "@/lib/categories";

function toSlug(name: string) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const EMPTY = { name: "", slug: "", image: "", sortOrder: "0", showInNav: false };

export function CategoryManager({ initialCategories }: { initialCategories: Category[] }) {
  const [categories, setCategories] = useState<Category[]>(initialCategories);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState<Category | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  function copyLink(link: string, id: string) {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(link);
      setCopiedId(id);
      toast.success("Permanent destination link copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(
      (c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q)
    );
  }, [categories, search]);

  const paginated = useMemo(() => {
    return filtered.slice((page - 1) * perPage, page * perPage);
  }, [filtered, page, perPage]);


  function set(key: keyof typeof EMPTY, value: any) {
    setForm((f) => {
      const next = { ...f, [key]: value };
      // Auto-generate slug from name when adding new
      if (key === "name" && !editing) next.slug = toSlug(value);
      return next;
    });
  }

  function startEdit(cat: Category) {
    setEditing(cat);
    setForm({
      name: cat.name,
      slug: cat.slug,
      image: cat.image ?? "",
      sortOrder: String(cat.sortOrder),
      showInNav: (cat as any).showInNav ?? false
    });
  }

  function cancel() {
    setEditing(null);
    setForm(EMPTY);
  }

  async function toggleHeaderNav(id: string, nextVal: boolean) {
    setCategories((prev) =>
      prev.map((c) => (c.id === id ? ({ ...c, showInNav: nextVal } as any) : c))
    );
    try {
      const res = await fetch(`/api/admin/categories/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ showInNav: nextVal })
      });
      if (!res.ok) throw new Error("Failed to update");
      toast.success(nextVal ? "Category displayed in header" : "Category hidden from header");
      if (typeof window !== "undefined") window.dispatchEvent(new Event("nav-updated"));
    } catch {
      setCategories((prev) =>
        prev.map((c) => (c.id === id ? ({ ...c, showInNav: !nextVal } as any) : c))
      );
      toast.error("Failed to update header navigation status.");
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error("Category name is required.");
      return;
    }
    setLoading(true);

    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim() || toSlug(form.name),
      image: form.image.trim() || undefined,
      sortOrder: Number(form.sortOrder) || 0,
      showInNav: form.showInNav
    };

    try {
      const url = editing ? `/api/admin/categories/${editing.id}` : "/api/admin/categories";
      const method = editing ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message ?? "Unable to save category.");

      const saved: Category = data.category;
      setCategories((prev) =>
        editing
          ? prev.map((c) => (c.id === editing.id ? saved : c))
          : [...prev, saved].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      );
      cancel();
      toast.success(editing ? "Category updated" : "Category created");
      if (typeof window !== "undefined") window.dispatchEvent(new Event("nav-updated"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unable to save category.");
    } finally {
      setLoading(false);
    }
  }

  async function deleteCategory(id: string) {
    const prev = categories;
    setCategories((list) => list.filter((c) => c.id !== id));
    setConfirmDeleteId(null);

    try {
      const res = await fetch(`/api/admin/categories/${id}`, { method: "DELETE" });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message ?? "Unable to delete category.");
      toast.success("Category deleted");
      if (typeof window !== "undefined") window.dispatchEvent(new Event("nav-updated"));
    } catch (err) {
      setCategories(prev);
      toast.error(err instanceof Error ? err.message : "Unable to delete category.");
    }
  }

  const inputCls =
    "h-11 w-full rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10";

  const inNavCount = categories.filter((c: any) => c.showInNav).length;

  return (
    <div className="space-y-6">
      {/* Quick link banner to Header Navigation Manager */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-xl border border-amazon-orange/30 bg-amber-50/60 p-4 dark:border-amazon-orange/20 dark:bg-amber-950/20">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-amazon-orange/20 p-2.5 text-amazon-orange shrink-0">
            <Sliders className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              Header Navigation Sub-bar Manager
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Currently <strong className="text-amazon-orange">{inNavCount} categories</strong> displayed in the storefront header bar. Organize, live preview, and toggle Today's Deals, Prime, and Compare.
            </p>
          </div>
        </div>
        <Link
          href="/admin/navigation"
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-amazon-navy px-4 py-2 text-xs font-bold text-white hover:bg-slate-800 transition shrink-0 shadow-sm"
        >
          Open Header Nav Manager →
        </Link>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <Tag className="h-6 w-6 text-amazon-orange" />
          <p className="mt-3 text-xs font-black uppercase text-slate-500">Total categories</p>
          <p className="mt-1 text-3xl font-black tracking-normal">{categories.length}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <Sliders className="h-6 w-6 text-emerald-600" />
          <p className="mt-3 text-xs font-black uppercase text-slate-500">Shown in Header Nav</p>
          <p className="mt-1 text-3xl font-black tracking-normal text-emerald-600">{inNavCount}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <p className="text-xs font-black uppercase text-slate-500">Storefront Sync</p>
          <p className="mt-3 text-3xl font-black tracking-normal text-amazon-green">Instant</p>
          <p className="mt-1 text-xs text-slate-500">Changes reflect live without restarting</p>
        </div>
      </div>

      {/* Primary Categories Permanent Links Quick Reference */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-white/10 dark:bg-slate-900">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
          <div>
            <h3 className="text-sm font-black text-slate-950 dark:text-white flex items-center gap-1.5">
              <span>⚡</span> Primary Categories Permanent Links
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Click any category to copy its permanent destination URL or create a targeted promotion banner.
            </p>
          </div>
          <Link
            href="/admin/promotions"
            className="inline-flex items-center gap-1 text-xs font-bold text-amazon-orange hover:underline shrink-0"
          >
            <Megaphone className="h-3.5 w-3.5" />
            <span>Open Promotion Banner Manager →</span>
          </Link>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {PRIMARY_CATEGORY_METAS.map((meta) => {
            const isCopied = copiedId === `primary-${meta.slug}`;
            return (
              <div
                key={meta.slug}
                className="flex items-center justify-between gap-2 rounded-lg border border-slate-200/80 bg-slate-50/60 p-2.5 text-xs dark:border-white/5 dark:bg-slate-800/40"
              >
                <div className="flex items-center gap-2 overflow-hidden">
                  <span className="text-base shrink-0">{meta.icon}</span>
                  <div className="overflow-hidden">
                    <p className="font-bold text-slate-900 dark:text-white truncate">
                      {meta.name}
                    </p>
                    <code className="text-[10px] font-mono text-slate-500 dark:text-slate-400 truncate block">
                      {meta.permanentLink}
                    </code>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => copyLink(meta.permanentLink, `primary-${meta.slug}`)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300"
                    title={`Copy permanent link for ${meta.name} promotion banner`}
                  >
                    {isCopied ? (
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5 text-slate-500" />
                    )}
                  </button>
                  <Link
                    href={`/admin/promotions?category=${encodeURIComponent(meta.name)}`}
                    className="inline-flex h-7 w-7 items-center justify-center rounded bg-amazon-orange/10 text-amazon-orange hover:bg-amazon-orange/20"
                    title={`Create promotion banner with destination: ${meta.permanentLink}`}
                  >
                    <Megaphone className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={onSubmit}
        className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-xl font-black text-slate-950 dark:text-white">
            {editing ? "Edit category" : "Add category"}
          </h2>
          {editing && (
            <button type="button" onClick={cancel} className="text-sm font-bold text-amazon-teal hover:underline">
              Cancel
            </button>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-bold">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Mobiles & Accessories"
              required
              className={inputCls}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-bold">Slug</label>
            <input
              value={form.slug}
              onChange={(e) => set("slug", e.target.value)}
              placeholder="auto-generated from name"
              className={inputCls}
            />
            <p className="mt-0.5 text-xs text-slate-400">Used in URLs — leave blank to auto-generate</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-bold">Image URL</label>
            <input
              type="url"
              value={form.image}
              onChange={(e) => set("image", e.target.value)}
              placeholder="https://example.com/category.jpg (optional)"
              className={inputCls}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-bold">Sort order</label>
            <input
              type="number"
              min="0"
              value={form.sortOrder}
              onChange={(e) => set("sortOrder", e.target.value)}
              placeholder="0"
              className={inputCls}
            />
            <p className="mt-0.5 text-xs text-slate-400">Lower = appears first in lists & header</p>
          </div>

          <div className="sm:col-span-2 pt-1">
            <label className="flex items-center gap-2.5 text-sm font-bold cursor-pointer">
              <input
                type="checkbox"
                checked={form.showInNav}
                onChange={(e) => set("showInNav", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-amazon-orange focus:ring-amazon-orange cursor-pointer"
              />
              <span>Display in Storefront Header Navigation Sub-bar</span>
            </label>
            <p className="mt-0.5 text-xs text-slate-400 ml-6.5">
              When checked, this category will appear directly in the top blue sub-header bar.
            </p>
          </div>

          {/* Permanent Link Info Preview */}
          <div className="sm:col-span-2 rounded-xl border border-amazon-orange/30 bg-amber-50/60 p-3.5 dark:border-amazon-orange/20 dark:bg-amber-950/20">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
              <div className="flex items-center gap-2 overflow-hidden">
                <Link2 className="h-4 w-4 text-amazon-orange shrink-0" />
                <span className="text-xs font-bold text-slate-900 dark:text-white shrink-0">
                  Permanent Destination URL:
                </span>
                <code className="truncate rounded bg-white px-2 py-0.5 font-mono text-xs font-bold text-slate-800 shadow-xs dark:bg-slate-900 dark:text-slate-200">
                  {getCategoryPermanentLink(form.name.trim() || "Category Name")}
                </code>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() =>
                    copyLink(
                      getCategoryPermanentLink(form.name.trim() || "Category Name"),
                      "form-preview"
                    )
                  }
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 shadow-xs"
                >
                  {copiedId === "form-preview" ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 text-slate-500" />
                  )}
                  <span>Copy Link for Banner</span>
                </button>
                {form.name.trim() && (
                  <Link
                    href={`/admin/promotions?category=${encodeURIComponent(form.name.trim())}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-amazon-orange px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-600 shadow-xs"
                  >
                    <Megaphone className="h-3.5 w-3.5" />
                    <span>Create Banner</span>
                  </Link>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              This canonical URL never changes and can be entered directly as the destination URL when creating promotion banners.
            </p>
          </div>
        </div>

        <div className="mt-5 flex gap-3">
          <Button type="submit" disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {editing ? "Update category" : "Add category"}
          </Button>
          {editing && (
            <Button type="button" variant="outline" onClick={cancel}>
              Cancel
            </Button>
          )}
        </div>
      </form>

      {/* Table */}
      {categories.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
          <Tag className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 font-bold">No categories yet</p>
          <p className="mt-1 text-sm text-slate-500">Add your first category above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search categories..."
                className="w-full rounded-md border border-slate-200 bg-white pl-9 pr-8 py-2 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium">
                Total: <strong className="text-slate-900 dark:text-white font-bold">{categories.length}</strong> categories
              </span>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="bg-slate-100 text-xs uppercase text-slate-500 dark:bg-white/10">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Slug</th>
                    <th className="px-4 py-3">Permanent Link (Destination URL)</th>
                    <th className="px-4 py-3">Header Bar</th>
                    <th className="px-4 py-3">Sort</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-slate-500">
                        No categories found matching &quot;{search}&quot;.
                      </td>
                    </tr>
                  ) : (
                    paginated.map((cat) => {
                      const isShown = (cat as any).showInNav;
                      const permLink = getCategoryPermanentLink(cat.name);
                      const isCopied = copiedId === `cat-${cat.id}`;
                      return (
                        <tr key={cat.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                          <td className="px-4 py-3 font-bold text-slate-950 dark:text-white">
                            {cat.name}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-500">{cat.slug}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 max-w-[280px]">
                              <code className="truncate rounded bg-slate-100 px-2 py-0.5 text-xs font-mono text-slate-700 dark:bg-white/10 dark:text-slate-300">
                                {permLink}
                              </code>
                              <button
                                type="button"
                                onClick={() => copyLink(permLink, `cat-${cat.id}`)}
                                className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300 shrink-0"
                                title="Copy permanent link for promotion banner"
                              >
                                {isCopied ? (
                                  <Check className="h-3.5 w-3.5 text-emerald-600" />
                                ) : (
                                  <Copy className="h-3.5 w-3.5 text-slate-500" />
                                )}
                              </button>
                              <a
                                href={permLink}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300 shrink-0"
                                title="Test storefront destination link"
                              >
                                <ExternalLink className="h-3.5 w-3.5 text-slate-500" />
                              </a>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => toggleHeaderNav(cat.id, !isShown)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition ${
                                isShown
                                  ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-950 dark:text-emerald-300"
                                  : "bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-400"
                              }`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${isShown ? "bg-emerald-600" : "bg-slate-400"}`} />
                              {isShown ? "In Header" : "Hidden"}
                            </button>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">{cat.sortOrder}</td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end items-center gap-1.5">
                              <Link
                                href={`/admin/promotions?category=${encodeURIComponent(cat.name)}`}
                                className="inline-flex items-center gap-1 rounded-md border border-amazon-orange/30 bg-amber-50 px-2 py-1 text-xs font-semibold text-amazon-orange hover:bg-amber-100 dark:border-amazon-orange/20 dark:bg-amber-950/30 dark:hover:bg-amber-950/50"
                                title="Create promotion banner for this category"
                              >
                                <Megaphone className="h-3.5 w-3.5" />
                                <span className="hidden md:inline">Banner</span>
                              </Link>
                              <button
                                type="button"
                                onClick={() => startEdit(cat)}
                                className="rounded-md p-2 hover:bg-slate-100 dark:hover:bg-white/10"
                                title="Edit"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                              {confirmDeleteId === cat.id ? (
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => deleteCategory(cat.id)}
                                    className="rounded-md bg-red-600 px-2 py-1 text-xs font-bold text-white hover:bg-red-700"
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDeleteId(null)}
                                    className="rounded-md px-2 py-1 text-xs font-bold text-slate-600 hover:bg-slate-100 dark:text-slate-300"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteId(cat.id)}
                                  className="rounded-md p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <AdminPagination
              currentPage={page}
              totalItems={filtered.length}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={setPerPage}
              itemLabel="categories"
            />
          </div>
        </div>
      )}

    </div>
  );
}
